import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import CustomDate from "../../../../components/common/CustomDate";
import {
  useGetTicketClickToExpandQuery,
  useTicketDashboardQuery,
} from "../../../../Services/dashboard/TicketDashboard";
import TableModal from "../../../../components/reusableComponent/TableModal";
import AnalyticsCardHeader from "./AnalyticsCardHeader";

function parseYyyyMmDdToLocalDate(s: string): Date {
  if (!s || s.length < 10) return new Date();
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7)) - 1;
  const d = Number(s.slice(8, 10));
  return new Date(y, m, d);
}

function formatDateToYyyyMmDd(date: Date): string {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function buildExpandViewDateRange(endDateStr: string) {
  const endDate = parseYyyyMmDdToLocalDate(endDateStr);
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - (30 - 1));
  return {
    startDate,
    endDate,
    key: "selection" as const,
  };
}

interface IProps {
  data: any;
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
  onTicketClick?: (ticket: any) => void;
  workflowId?: string | null;
}

const PendingVsResolvedTickets: React.FC<IProps> = ({
  data,
  startDate,
  endDate,
  teamId,
  onTicketClick,
  workflowId,
}) => {
  const navigate = useNavigate();
  const [fullscreenChart, setFullscreenChart] = useState<any>(null);
  const [modalDateRange, setModalDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  useEffect(() => {
    if (fullscreenChart && endDate) {
      setModalDateRange([buildExpandViewDateRange(endDate)]);
    }
  }, [fullscreenChart, endDate]);

  const modalRangeStrings = useMemo(
    () => ({
      start: modalDateRange?.[0]?.startDate
        ? formatDateToYyyyMmDd(modalDateRange[0].startDate)
        : "",
      end: modalDateRange?.[0]?.endDate
        ? formatDateToYyyyMmDd(modalDateRange[0].endDate)
        : "",
    }),
    [modalDateRange],
  );

  const { data: modalOverviewData } = useTicketDashboardQuery(
    {
      dashboardType: "analytics",
      selectedTeam: teamId ?? "",
      selectedWorkFlow: workflowId ?? "",
      graphStartDate: modalRangeStrings.start,
      graphEndDate: modalRangeStrings.end,
    },
    {
      skip:
        !fullscreenChart ||
        !teamId ||
        !modalRangeStrings.start ||
        !modalRangeStrings.end,
    },
  );

  const expandStart = fullscreenChart ? modalRangeStrings.start : startDate ?? "";
  const expandEnd = fullscreenChart ? modalRangeStrings.end : endDate ?? "";

  // ── Click-to-expand state ─────────────────────────────────────────────────
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null); // "2024-01-15:pending"
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [clickToExpandPage, setClickToExpandPage] = useState(1);

  const getPath = (x: number, y: number, width: number, height: number) => {
    return `M${x},${y + height}C${x + width / 3},${y + height} ${
      x + width / 2
    },${y + height / 3}
    ${x + width / 2}, ${y}
    C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height} ${
      x + width
    }, ${y + height}
    Z`;
  };

  const TriangleBar = (props: any) => {
    const { fill, x, y, width, height } = props;
    return (
      <path
        d={getPath(Number(x), Number(y), Number(width), Number(height))}
        stroke="none"
        fill={fill}
      />
    );
  };

  const parseToISODate = (dateStr: string, referenceYear?: string): string => {
    // Already in DD-MM-YYYY format — return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const year = referenceYear
      ? new Date(referenceYear).getFullYear()
      : new Date().getFullYear();
    const d = new Date(`${dateStr} ${year}`);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${dd}-${mm}-${year}`;
  };
  const paramDate = (dateStr: string, referenceYear?: string): string => {
    // Already in YYYY-MM-DD format — return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const year = referenceYear
      ? new Date(referenceYear).getFullYear()
      : new Date().getFullYear();
    const d = new Date(`${dateStr} ${year}`);
    if (isNaN(d.getTime())) return dateStr;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${year}-${mm}-${dd}`;
  };

  // formatDisplayDate — ONLY for visual display on X-axis and tooltip ("28 May")
  const formatDisplayDate = (
    dateStr: string,
    referenceYear?: string,
  ): string => {
    if (!dateStr) return dateStr;

    const trimmed = dateStr.trim();

    // Already day-first: "28 May", "1 Jun"
    if (/^\d{1,2}\s+[A-Za-z]{3}\b/.test(trimmed)) return trimmed;

    let d: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      d = parseYyyyMmDdToLocalDate(trimmed);
    } else {
      // API display format: "May 28", "Jun 01"
      const year = referenceYear
        ? parseYyyyMmDdToLocalDate(referenceYear).getFullYear()
        : new Date().getFullYear();
      d = new Date(`${trimmed} ${year}`);
    }

    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const chartData =
    data?.tickets_bar_graph?.dates?.map((date: string, index: number) => ({
      displayDate: formatDisplayDate(date, startDate), // "28 May" — for X-axis & tooltip only
      rawDate: parseToISODate(date, startDate), // "DD-MM-YYYY" — always used for x-axis
      paramDate: paramDate(date, startDate), // "YYYY-MM-DD" — always used for API
      pending: data.tickets_bar_graph.pending_tickets[index]?.count || 0,
      resolved: data.tickets_bar_graph.resolved_tickets[index]?.count || 0,
    })) || [];

  const modalChartData = useMemo(() => {
    const src =
      fullscreenChart && modalOverviewData != null ? modalOverviewData : data;
    if (!src?.tickets_bar_graph?.dates?.length) return [];
    const refDate = modalRangeStrings.start || startDate;
    return src.tickets_bar_graph.dates.map((date: string, index: number) => ({
      displayDate: formatDisplayDate(date, refDate),
      rawDate: parseToISODate(date, refDate),
      pending: src.tickets_bar_graph.pending_tickets[index]?.count || 0,
      resolved: src.tickets_bar_graph.resolved_tickets[index]?.count || 0,
    }));
  }, [
    fullscreenChart,
    modalOverviewData,
    data,
    modalRangeStrings.start,
    startDate,
  ]);

  const totalPending = chartData.reduce(
    (sum: number, item: any) => sum + item.pending,
    0,
  );
  const totalResolved = chartData.reduce(
    (sum: number, item: any) => sum + item.resolved,
    0,
  );
  const totalTickets = totalPending + totalResolved;

  const pendingColor = "#FF9800";
  const resolvedColor = "#4CAF50";

  // ── Click-to-expand query ─────────────────────────────────────────────────
  // value format: "2024-01-15:pending" or "2024-01-15:resolved"
  const clickToExpandParams =
    tableModalOpen && selectedValue && teamId && expandStart && expandEnd
      ? {
          graph_name: "tickets_bar_graph",
          value: selectedValue,
          start_date: expandStart,
          end_date: expandEnd,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
          workflow_id: workflowId || null,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetTicketClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams },
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 15;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedValue]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  // recharts passes (data, index) — we use index to look up our chartData directly
  // so we always get rawDate ("YYYY-MM-DD") regardless of what recharts puts in entry
  const handleBarClick = (
    entry: any,
    index: number,
    status: "pending" | "resolved",
    rows: typeof chartData = chartData,
  ) => {
    if (!teamId || !expandStart || !expandEnd) return;
    const item = rows[index];
    if (!item?.paramDate) return;
    const value = `${item.paramDate}:${status}`;
    const label = `${
      status.charAt(0).toUpperCase() + status.slice(1)
    } tickets on ${item.displayDate}`;
    setSelectedValue(value);
    setSelectedLabel(label);
    setTableModalOpen(true);
    setClickToExpandPage(1);
  };

  const handleCloseModal = () => {
    setTableModalOpen(false);
    setSelectedValue(null);
    setSelectedLabel("");
    setClickToExpandPage(1);
  };

  const clickable = !!(teamId && expandStart && expandEnd);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "16px 18px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            minWidth: "200px",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: "2px solid #e2e8f0",
            }}
          >
            <span
              style={{ fontWeight: 700, fontSize: "15px", color: "#1e293b" }}
            >
              {payload[0].payload.displayDate}
            </span>
          </div>
          {clickable && (
            <p
              style={{
                fontSize: "11px",
                color: "#94a3b8",
                margin: "0 0 8px 0",
              }}
            >
              Click bar to see ticket details
            </p>
          )}
          {payload.map((entry: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: index < payload.length - 1 ? "8px" : "0",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    borderRadius: "4px",
                    backgroundColor: entry.color,
                    boxShadow: `0 0 8px ${entry.color}40`,
                  }}
                />
                <span
                  style={{
                    fontSize: "13px",
                    color: "#64748b",
                    fontWeight: 600,
                  }}
                >
                  {entry.name}
                </span>
              </div>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#1e293b",
                  marginLeft: "16px",
                }}
              >
                {entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const downloadCSV = () => {
    const headers = ["Date", "Pending Tickets", "Resolved Tickets"];
    const rows = chartData.map((item: any) => [
      item.rawDate,
      item.pending,
      item.resolved,
    ]);
    const csv = [headers, ...rows].map((row: any) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pending_vs_resolved_tickets.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const theme = {
    colors: {
      surface: "#ffffff",
      primary: "#6366F1",
      text: "#1f2937",
      textSecondary: "#4b5563",
      textMuted: "#6b7280",
    },
  };

  const styles = {
    expandButton: {
      background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      padding: "12px 20px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      transition: "all 0.3s",
      boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  const modalTableData = modalChartData.map((item: any) => ({
    date: item.displayDate,
    pending: item.pending,
    resolved: item.resolved,
    total: item.pending + item.resolved,
  }));

  const ResponsiveChartContainer = ResponsiveContainer as any;
  const BarChartContainer = BarChart as any;
  const BarContainer = Bar as any;
  const LegendContainer = Legend as any;
  const XAxisContainer = XAxis as any;
  const YAxisContainer = YAxis as any;

  const getXAxisProps = (dataLength: number) => ({
    dataKey: "displayDate",
    tick: { fontSize: 12, fill: "#64748b", fontWeight: 500 },
    tickLine: false,
    axisLine: { stroke: "#cbd5e1", strokeWidth: 2 },
    interval: 0,
    height: dataLength > 4 ? 56 : 48,
    tickMargin: 8,
    label: {
      value: "Date",
      position: "insideBottom",
      offset: -2,
      style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
    },
  });

  const getChartMargin = (dataLength: number) => ({
    top: 20,
    right: 30,
    left: 10,
    bottom: dataLength > 4 ? 48 : 40,
  });

  const renderChart = (
    height: any = "100%",
    rows: typeof chartData = chartData,
  ) => (
    <ResponsiveChartContainer width="100%" height={height}>
      <BarChartContainer
        data={rows}
        margin={getChartMargin(rows.length)}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="#e2e8f0"
          vertical={false}
        />
        <XAxisContainer {...getXAxisProps(rows.length)} />
        <YAxisContainer
          tick={{ fontSize: 12, fill: "#64748b", fontWeight: 500 }}
          tickLine={false}
          axisLine={{ stroke: "#cbd5e1", strokeWidth: 2 }}
          domain={[
            0,
            (dataMax: number) => {
              if (!dataMax || dataMax === 0) return 3;
              return Math.ceil(dataMax / 2) * 2;
            },
          ]}
          label={{
            value: "Ticket count",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: 13, fill: "#475569", fontWeight: 600 },
          }}
          allowDecimals={false}
          tickCount={4}
        />
        <Tooltip
          content={<CustomTooltip />}
          cursor={{ fill: "rgba(99, 102, 241, 0.08)" }}
        />
        <LegendContainer
          verticalAlign="bottom"
          height={10}
          iconType="circle"
          wrapperStyle={{ paddingTop: "14px" }}
          formatter={(value: string) => (
            <span style={{ color: "#475569", fontWeight: 600 }}>{value}</span>
          )}
        />
        <BarContainer
          dataKey="pending"
          name="Pending"
          fill={pendingColor}
          shape={<TriangleBar />}
          onClick={(data: any, index: number) =>
            handleBarClick(data, index, "pending", rows)
          }
          style={{ cursor: clickable ? "pointer" : "default" }}
        />
        <BarContainer
          dataKey="resolved"
          name="Resolved"
          fill={resolvedColor}
          shape={<TriangleBar />}
          onClick={(data: any, index: number) =>
            handleBarClick(data, index, "resolved", rows)
          }
          style={{ cursor: clickable ? "pointer" : "default" }}
        />
      </BarChartContainer>
    </ResponsiveChartContainer>
  );

  const FullscreenModal = ({
    isOpen,
    onClose,
    title,
  }: any) => {
    if (!isOpen) return null;
    const modalPendingTotal = modalChartData.reduce(
      (sum: number, item: any) => sum + item.pending,
      0,
    );
    const modalResolvedTotal = modalChartData.reduce(
      (sum: number, item: any) => sum + item.resolved,
      0,
    );
    const tableKeys =
      modalTableData && modalTableData.length > 0
        ? Object.keys(modalTableData[0])
        : [];
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.75)",
          zIndex: 9999,
          padding: "24px",
          overflow: "auto",
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: theme.colors.surface,
            borderRadius: "20px",
            width: "95%",
            maxWidth: "1600px",
            margin: "0 auto",
            padding: "32px",
            position: "relative",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "28px",
              paddingBottom: "20px",
              borderBottom: "2px solid #e2e8f0",
            }}
          >
            <div>
              <h2
                style={{ margin: 0, color: theme.colors.text, fontWeight: 700 }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: "8px 0 0 0",
                  color: theme.colors.textMuted,
                  fontSize: "14px",
                }}
              >
                Ticket trends over time: {modalPendingTotal} pending,{" "}
                {modalResolvedTotal} resolved
              </p>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                flexShrink: 0,
              }}
            >
              <div style={{ position: "relative", zIndex: 10002 }}>
                <CustomDate
                  dateRange={modalDateRange}
                  setDateRange={setModalDateRange}
                  unrestricted
                />
              </div>
              <button
                onClick={onClose}
                style={{
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "44px",
                  height: "44px",
                  fontSize: "24px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s",
                  boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "rotate(90deg) scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "rotate(0deg) scale(1)";
                }}
              >
                ×
              </button>
            </div>
          </div>
          <div style={{ marginBottom: "32px" }}>
            <div
              style={{
                width: "100%",
                height: "550px",
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
              }}
            >
              {renderChart("100%", modalChartData)}
            </div>
          </div>
          {modalTableData && modalTableData.length > 0 && (
            <div style={{ marginTop: "32px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: theme.colors.text,
                    fontSize: "1.5em",
                    fontWeight: 700,
                  }}
                >
                  📊 Ticket data
                </h3>
                <span
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {modalTableData.length} dates
                </span>
              </div>
              <div
                style={{
                  overflowX: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  maxHeight: "450px",
                  overflowY: "auto",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "14px",
                  }}
                >
                  <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                    <tr style={{ backgroundColor: "#f8fafc" }}>
                      {tableKeys.map((key) => (
                        <th
                          key={key}
                          style={{
                            padding: "14px 18px",
                            textAlign: "left",
                            borderBottom: "2px solid #cbd5e1",
                            fontWeight: 700,
                            color: theme.colors.textSecondary,
                            whiteSpace: "nowrap",
                            position: "sticky",
                            top: 0,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          {key.toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {modalTableData.map((row: any, index: number) => (
                      <tr
                        key={index}
                        style={{
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#f8fafc",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#eef2ff")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            index % 2 === 0 ? "#ffffff" : "#f8fafc")
                        }
                      >
                        {tableKeys.map((key) => (
                          <td
                            key={key}
                            style={{
                              padding: "14px 18px",
                              borderBottom: "1px solid #e2e8f0",
                              color: theme.colors.textSecondary,
                            }}
                          >
                            {row[key] !== null && row[key] !== undefined
                              ? row[key] === 0
                                ? "-"
                                : String(row[key])
                              : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const hasData = chartData.length > 0;

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        height: "100%",
        width: "100%",
      }}
    >
      <div style={{ padding: "32px" }}>
        <AnalyticsCardHeader
          icon={
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6366f1"
              strokeWidth="2"
            >
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          }
          title="Pending v/s Resolved"
          subtitle={
            <>
              {totalPending} pending • {totalResolved} resolved • {totalTickets}{" "}
              tickets
              {clickable && (
                <span style={{ color: "#94a3b8" }}>
                  {" "}
                  • Click bar to see ticket details
                </span>
              )}
            </>
          }
          actions={
            <>
              <button
                onClick={downloadCSV}
                style={{
                  padding: "10px 18px",
                  backgroundColor: "#f8fafc",
                  border: "2px solid #e2e8f0",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#64748b",
                  transition: "all 0.3s",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#eef2ff";
                  e.currentTarget.style.borderColor = "#6366f1";
                  e.currentTarget.style.color = "#6366f1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.color = "#64748b";
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                CSV
              </button>
              <button
                onClick={() =>
                  setFullscreenChart({
                    title: "Pending v/s Resolved",
                  })
                }
                style={styles.expandButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(99, 102, 241, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(99, 102, 241, 0.3)";
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                </svg>
                Expand View
              </button>
            </>
          }
        />

        {hasData ? (
          <div
            style={{
              width: "100%",
              height: "550px",
              backgroundColor: "#fafbfc",
              padding: "24px 24px 0 24px",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
            }}
          >
            {renderChart()}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "550px",
              backgroundColor: "#fafbfc",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
            }}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                backgroundColor: "#f3f4f6",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <svg
                style={{ width: "32px", height: "32px", color: "#9ca3af" }}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="12" y1="8" x2="12" y2="16" />
              </svg>
            </div>
            <h3
              style={{ color: "#64748b", fontSize: "1.25em", fontWeight: 600 }}
            >
              No data available
            </h3>
            <p style={{ color: "#94a3b8", fontSize: "14px", marginTop: "8px" }}>
              There are no tickets to display in this chart.
            </p>
          </div>
        )}
      </div>

      <FullscreenModal
        isOpen={!!fullscreenChart}
        onClose={() => setFullscreenChart(null)}
        title={fullscreenChart?.title}
      />

      {/* ── Click-to-expand modal (replaces custom TicketModal) ── */}
      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={handleCloseModal}
        title={selectedLabel || "Tickets"}
        contentLabel="Pending vs resolved click-to-expand"
        emptyMessage="No tickets found."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx: number) => row?.ticket_id ?? row?.id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const ticketId = row?.ticket_id ?? row?.id;
          if (ticketId != null) {
            handleCloseModal();
            navigate(`/ticket/information/${ticketId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          {
            label: "Ticket ID",
            render: (row: any) => row?.ticket_id ?? row?.id ?? "—",
          },
          { label: "Title", render: (row: any) => row?.title ?? "—" },
          {
            label: "Contact Name",
            render: (row: any) => row?.contact_name ?? "—",
          },
          {
            label: "Contact Email",
            render: (row: any) => row?.contact_email ?? "—",
          },
          {
            label: "Contact Phone",
            render: (row: any) => row?.contact_phone ?? "—",
          },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = String(d.getHours()).padStart(2, "0");
              const min = String(d.getMinutes()).padStart(2, "0");
              const ss = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </div>
  );
};

export default PendingVsResolvedTickets;
